import { useEffect, useMemo } from 'react'
import { useMutation, useQuery } from 'react-query'
import { AxiosResponse } from 'axios'

import { PATH } from '../../constants/global'
import { useAPI } from '..'
import { NotificationTypes } from '../../types'

import {
   UseNotifications,
   UseNotificationsOptions,
   NotificationsResponse,
   NotificationParams,
   UpdateNotificationParams,
   DismissNotificationParams,
} from './types'

export default function ({
   itemsPerPage = 10,
   onChange,
   totalItems = 0,
   notificationType,
   currentPage = 1,
   toolId,
}: UseNotificationsOptions): UseNotifications {
   const request = useAPI()

   async function getNotificationsFromAPI(
      params: NotificationParams
   ): Promise<AxiosResponse<NotificationsResponse>> {
      return await request.get(PATH.DASHBOARD.NOTIFICATIONS, {
         params,
      })
   }

   const { data, isLoading, isRefetching, refetch } = useQuery(
      ['notifications', { notificationType, currentPage, toolId }],
      () => {
         const params: NotificationParams = {
            skip: (currentPage - 1) * itemsPerPage,
            take: itemsPerPage,
            category:
               notificationType === NotificationTypes.Global
                  ? 1
                  : notificationType,
            returnCompanyNotifications:
               notificationType === NotificationTypes.Global,
            toolId: toolId,
         }
         return getNotificationsFromAPI(params)
      }
   )

   useEffect(() => {
      refetch()
   }, [currentPage])

   async function dismissNotificationInAPI(id: number): Promise<AxiosResponse> {
      const params: DismissNotificationParams = {
         id,
         accept: false,
         delete: true,
      }
      return await request.post(PATH.DASHBOARD.NOTIFICATION_DISMISS, params)
   }

   const {
      mutate: mutateDismissNotification,
      isLoading: isDismissing,
      isSuccess: isDismissSuccess,
   } = useMutation(dismissNotificationInAPI, {
      onSettled: () => {
         refetch()
         if (onChange) {
            onChange()
         }
      },
   })

   const dismiss = (id: number) => mutateDismissNotification(id)

   const deleteGlobal = (id: number) => mutateDismissNotification(id)

   async function dismissAllInfoInAPI(): Promise<AxiosResponse> {
      return await request.post(PATH.DASHBOARD.DIMISS_ALL_INFO)
   }

   const {
      mutate: mutateDismissAllInfo,
      isLoading: isDismissingAllInfo,
      isSuccess: isDismissAllInfoSuccess,
   } = useMutation(dismissAllInfoInAPI, {
      onSettled: () => {
         refetch()
         if (onChange) {
            onChange()
         }
      },
   })

   const dismissAllInfo = () => mutateDismissAllInfo()

   async function updateNotificationInAPI(
      params: UpdateNotificationParams
   ): Promise<AxiosResponse> {
      return await request.post(PATH.DASHBOARD.NOTIFICATION_DISMISS, params)
   }

   const {
      mutate: mutateAcceptRejectNotification,
      isSuccess: isAcceptRejectSuccess,
      isLoading: isAcceptRejectLoading,
   } = useMutation(updateNotificationInAPI, {
      onSettled: () => {
         refetch()
         if (onChange) {
            onChange()
         }
      },
   })

   const acceptReject = (id: number, accept: boolean) =>
      mutateAcceptRejectNotification({ id, accept })

   const list = data?.data?.Data || []

   const isLastPage = useMemo(() => {
      const numberOfItems = list.length
      return numberOfItems % itemsPerPage !== 0 || numberOfItems === totalItems
   }, [list, itemsPerPage, totalItems])

   return {
      isLastPage: isLastPage,
      isLoading:
         isLoading ||
         isRefetching ||
         isDismissing ||
         isAcceptRejectLoading ||
         isDismissingAllInfo,
      isLoadingMore: isRefetching,
      list,
      acceptReject,
      dismiss,
      deleteGlobal,
      isDismissSuccess,
      isAcceptRejectSuccess,
      dismissAllInfo,
      isDismissAllInfoSuccess,
      refetch: refetch,
   }
}
